// Figma: https://www.figma.com/design/iYlbYhvyJjh7X00UAcqCWt/About-page?node-id=13-5273
import { Box, Flex, Image, Text } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";


const shineSpin = keyframes`
  0% { background-position: 0 0; }
  100% { background-position: 200% 0; }
`;

interface FeatureCardProps {
  imageSrc: string;
  description: string;
}

export function FeatureCard({ imageSrc, description }: FeatureCardProps) {
  return (
    <Flex direction="column" gap="0" align="center" w="100%" role="group">
      {/* Wrapper provides a solid charcoal shield behind the masked card so
          the halftone decorations sitting behind the grid don't bleed
          through where the card fades out. */}
      <Box position="relative" w="100%">
        {/* Shield — solid charcoal matching the page bg, only visible
            where the masked card above fades to transparent. */}
        <Box
          position="absolute"
          inset={0}
          borderTopLeftRadius="16px"
          borderTopRightRadius="16px"
          bg="charcoal.charcoal"
          pointerEvents="none"
        />
        {/* Image with shine border + gradient overlay */}
        <Box
          position="relative"
          borderTopLeftRadius="16px"
          borderTopRightRadius="16px"
          overflow="hidden"
          w="100%"
          isolation="isolate"
          boxShadow="0 -4px 16px rgba(0,0,0,0.25), 0 -8px 32px rgba(0,0,0,0.15)"
          border="1px solid rgba(255,255,255,0.1)"
          borderBottom="none"
          sx={{
            // Vertical fade mask applied to the entire card — fades the
            // static border, the animated shine ring, and the image in
            // lockstep at the bottom. Matches the charcoal overlay curve
            // below (solid at the bottom ~3% of the card, ramping to fully
            // visible by ~60% from the bottom). The shield Box above sits
            // behind this element to cover the halftones that would
            // otherwise show through the transparent area.
            WebkitMaskImage:
              "linear-gradient(to top, transparent 3%, #000 60%)",
            maskImage: "linear-gradient(to top, transparent 3%, #000 60%)",
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              borderTopLeftRadius: "16px",
              borderTopRightRadius: "16px",
              padding: "1px",
              background:
                "radial-gradient(transparent, transparent, rgba(251,238,232,0.5), transparent, transparent)",
              backgroundSize: "200% 200%",
              WebkitMask:
                "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
              WebkitMaskComposite: "xor",
              maskComposite: "exclude",
              pointerEvents: "none",
              zIndex: 3,
              animation: `${shineSpin} 6s linear infinite`,
            },
          }}
        >
          <Image
            src={imageSrc}
            alt={description}
            w="100%"
            h="257px"
            objectFit="cover"
          />
          {/* Gradient fade to background at bottom — sits above shine border */}
          <Box
            position="absolute"
            bottom="-1px"
            left="0"
            right="0"
            h="60%"
            bgGradient="linear(to-t, charcoal.charcoal 0%, charcoal.charcoal 5%, transparent)"
            pointerEvents="none"
            zIndex={4}
          />
        </Box>
      </Box>

      {/* Description text (13:5279) — pulled up into the masked fade zone
          below the image so it sits closer to the visible image content.
          position="relative" is required so it paints above the positioned
          image wrapper above (later sibling wins in the positioned layer). */}
      <Text
        mt="-24px"
        position="relative"
        fontSize="20px"
        fontWeight="400"
        color="dune.700"
        lineHeight="1.4"
        textAlign="center"
        whiteSpace="pre-line"
      >
        {description}
      </Text>
    </Flex>
  );
}
